import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Card from "components/card";
import { MdClose } from "react-icons/md";
import { toast } from 'react-hot-toast';
import { Toaster } from 'react-hot-toast';

const ConsumeForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const zone = location.state?.zone;

  const [consumeForm, setConsumeForm] = useState({
    origin: "",
    size: "",
    currentStock: 0,
    bagsConsumed: "",
    quantityConsumed: "",
    defectiveRate: "",
    moistureLevel: "",
    spottedRate: "",
    outturn: "",
    rottenContent: "",
    date: new Date().toISOString().split('T')[0],
    purity: ""
  });

  useEffect(() => {
    if (!zone) {
      toast.error('No zone data provided');
      navigate('/admin/warehouse/consume');
    }
  }, [zone, navigate]);

  const origins = [...new Set(zone?.cashews.map(c => c.origin) || [])];
  const sizes = zone?.cashews
    .filter(c => c.origin === consumeForm.origin)
    .map(c => c.size) || [];

  const handleOriginChange = (e) => {
    const origin = e.target.value;
    setConsumeForm(prev => ({
      ...prev,
      origin,
      size: "",
      currentStock: 0
    }));
  };

  const handleSizeChange = (e) => {
    const size = e.target.value;

    // Look up current stock directly from the zone's cashews data
    const currentStock = zone?.cashews
      .filter(c => c.origin === consumeForm.origin && c.size === size)
      .reduce((sum, c) => sum + c.weight, 0) ?? 0;

    setConsumeForm(prev => ({
      ...prev,
      size,
      currentStock
    }));
  };

  // Add date validation function
  const validateDateFormat = (dateString) => {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    return regex.test(dateString);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Submit button clicked");
    
    const requiredFields = [
      'origin',
      'size',
      'bagsConsumed',
      'quantityConsumed',
      'defectiveRate',
      'moistureLevel',
      'spottedRate',
      'outturn',
      'rottenContent',
      'date',
      'purity'
    ];

    const emptyFields = requiredFields.filter(field => !consumeForm[field]);
    
    if (emptyFields.length > 0) {
      console.log("Empty fields:", emptyFields);
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate date format
    if (!validateDateFormat(consumeForm.date)) {
      toast.error('Invalid date format. Please use YYYY-MM-DD format');
      return;
    }

    // Check if quantity consumed is more than current stock
    if (parseFloat(consumeForm.quantityConsumed) > consumeForm.currentStock) {
      toast.error(`Quantity consumed (${consumeForm.quantityConsumed} kg) cannot be more than current stock (${consumeForm.currentStock} kg)`);
      return;
    }

    // Format the data for the API - using exact format from sample
    const consumeData = {
      origin: consumeForm.origin,
      size: `size_${consumeForm.size.split(' ')[1]}`, // Convert "Size 28" to "size_28"
      stacking_zone: zone.name,
      date: consumeForm.date,
      bags_consumed: parseInt(consumeForm.bagsConsumed),
      quantity_consumed: parseFloat(consumeForm.quantityConsumed),
      defective_rate: parseFloat(consumeForm.defectiveRate),
      moisture_level: parseFloat(consumeForm.moistureLevel),
      spotted_rate: parseFloat(consumeForm.spottedRate),
      outturn: parseFloat(consumeForm.outturn),
      rotten_content: parseFloat(consumeForm.rottenContent),
      purity: parseFloat(consumeForm.purity)
    };

    console.log("Sending data:", consumeData);

    // Show loading toast
    const loadingToast = toast.loading('Processing consumption...');

    // Call the API
    fetch('https://usibras-web-backend.onrender.com/api/consume-stock', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(consumeData),
    })
      .then(async response => {
        console.log("Response status:", response.status);
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Failed to consume stock');
        }
        return data;
      })
      .then(data => {
        console.log("Success data:", data);
        toast.dismiss(loadingToast);
        toast.success(`${data.message}. Remaining stock: ${data.remaining_weight} kg`);
        navigate('/admin/warehouse/consume');
      })
      .catch(error => {
        console.error("Fetch error:", error);
        toast.dismiss(loadingToast);
        toast.error(error.message || 'Failed to consume stock');
      });
  };

  if (!zone) return null;

  return (
    <div className="mt-5 h-full">
      <Toaster position="top-right" />
      <Card extra={"w-full h-full p-6"}>
        <div className="flex items-center justify-between border-b pb-4">
          <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
            Consume from Zone {zone.name}
          </h2>
          <button
            onClick={() => navigate('/admin/warehouse/consume')}
            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-navy-600"
          >
            <MdClose className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Date Selection */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Date (YYYY-MM-DD) <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={consumeForm.date}
                onChange={(e) => setConsumeForm(prev => ({
                  ...prev,
                  date: e.target.value
                }))}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                required
                pattern="\d{4}-\d{2}-\d{2}"
                title="Please enter date in YYYY-MM-DD format"
              />
            </div>

            {/* Origin Selection */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Origin <span className="text-red-500">*</span>
              </label>
              <select
                value={consumeForm.origin}
                onChange={handleOriginChange}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                required
              >
                <option value="">Select Origin</option>
                {origins.map((origin) => (
                  <option key={origin} value={origin}>
                    {origin}
                  </option>
                ))}
              </select>
            </div>

            {/* Size Selection */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Size <span className="text-red-500">*</span>
              </label>
              <select
                value={consumeForm.size}
                onChange={handleSizeChange}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                required
                disabled={!consumeForm.origin}
              >
                <option value="">Select Size</option>
                {sizes.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            {/* Current Stock */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Current Stock (kg)
              </label>
              <input
                type="text"
                value={consumeForm.currentStock}
                className="w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-2 text-gray-700 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                disabled
              />
            </div>

            {/* Bags Consumed */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Bags Consumed <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={consumeForm.bagsConsumed}
                onChange={(e) => setConsumeForm(prev => ({
                  ...prev,
                  bagsConsumed: e.target.value
                }))}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                required
                min="1"
              />
            </div>

            {/* Quantity Consumed */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Quantity Consumed (kg) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={consumeForm.quantityConsumed}
                onChange={(e) => setConsumeForm(prev => ({
                  ...prev,
                  quantityConsumed: e.target.value
                }))}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                required
                min="0"
                step="0.01"
              />
            </div>

            {/* Defective Rate */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              TDR (%) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={consumeForm.defectiveRate}
                onChange={(e) => setConsumeForm(prev => ({
                  ...prev,
                  defectiveRate: e.target.value
                }))}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                required
                min="0"
                max="100"
                step="0.01"
              />
            </div>

            {/* Moisture Level */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Moisture Level (%)
              </label>
              <input
                type="number"
                value={consumeForm.moistureLevel}
                onChange={(e) => setConsumeForm(prev => ({
                  ...prev,
                  moistureLevel: e.target.value
                }))}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                min="0"
                max="100"
                step="0.01"
              />
            </div>

            {/* Spotted Rate */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Spotted Rate (%)
              </label>
              <input
                type="number"
                value={consumeForm.spottedRate}
                onChange={(e) => setConsumeForm(prev => ({
                  ...prev,
                  spottedRate: e.target.value
                }))}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                min="0"
                max="100"
                step="0.01"
              />
            </div>

            {/* Outturn */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Outturn (%)
              </label>
              <input
                type="number"
                value={consumeForm.outturn}
                onChange={(e) => setConsumeForm(prev => ({
                  ...prev,
                  outturn: e.target.value
                }))}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                min="0"
                max="100"
                step="0.01"
              />
            </div>

            {/* Rotten Content */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Rotten Content (%)
              </label>
              <input
                type="number"
                value={consumeForm.rottenContent}
                onChange={(e) => setConsumeForm(prev => ({
                  ...prev,
                  rottenContent: e.target.value
                }))}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                min="0"
                max="100"
                step="0.01"
              />
            </div>

            {/* Purity */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Purity (%) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={consumeForm.purity}
                onChange={(e) => setConsumeForm(prev => ({
                  ...prev,
                  purity: e.target.value
                }))}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                required
                min="0"
                max="100"
                step="0.01"
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="mt-8 flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/admin/warehouse/consume')}
              className="rounded-lg border border-gray-300 bg-white px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-lg bg-brand-500 px-6 py-2 text-sm font-medium text-white hover:bg-brand-600"
            >
              Consume
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ConsumeForm; 